import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Users, Search, Download, Eye, Ban, UserCheck } from "lucide-react";

export default function AdminUsersTab({ apiUrl, getAuthHeaders }) {
  const credFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: "include" });

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userDetail, setUserDetail] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await credFetch(`${apiUrl}/api/admin/users?search=${userSearch}`, { headers: getAuthHeaders(), credentials: "include" });
      const data = await res.json();
      if (data.success) setUsers(data.users);
    } catch (error) { console.error("Users error:", error); }
    setLoading(false);
  };

  const banUser = async (userId) => {
    if (!window.confirm("Are you sure you want to ban this user?")) return;
    try {
      const res = await credFetch(`${apiUrl}/api/admin/users/${userId}/ban`, { method: "PUT", headers: getAuthHeaders(), credentials: "include", credentials: "include" });
      if (res.ok) { toast.success("User banned"); loadUsers(); }
    } catch (error) { toast.error("Failed to ban user"); }
  };

  const unbanUser = async (userId) => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/users/${userId}/unban`, { method: "PUT", headers: getAuthHeaders(), credentials: "include", credentials: "include" });
      if (res.ok) { toast.success("User unbanned"); loadUsers(); }
    } catch (error) { toast.error("Failed to unban user"); }
  };

  const getUserDetails = async (userId) => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/users/${userId}/details`, { headers: getAuthHeaders(), credentials: "include" });
      const data = await res.json();
      if (data.success) { setUserDetail(data); setShowUserDetail(true); }
    } catch (error) { toast.error("Failed to load user details"); }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/users/${userId}/role`, { method: "PUT", headers: getAuthHeaders(), credentials: "include", body: JSON.stringify({ role: newRole }) });
      const data = await res.json();
      if (data.success) { toast.success(`User role updated to ${newRole}`); loadUsers(); }
      else toast.error(data.detail || "Failed to update role");
    } catch (error) { toast.error("Failed to update user role"); }
  };

  const updateUserPlan = async (userId, newPlan, days = 365) => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/users/${userId}/plan?plan=${newPlan}&days=${days}`, { method: "PUT", headers: getAuthHeaders(), credentials: "include", credentials: "include" });
      const data = await res.json();
      if (data.success) { toast.success(data.message || `User plan updated to ${newPlan}`); loadUsers(); }
      else toast.error(data.detail || "Failed to update plan");
    } catch (error) { toast.error("Failed to update user plan"); }
  };

  const handleBulkAction = async (action) => {
    if (selectedUsers.length === 0) { toast.error("No users selected"); return; }
    try {
      const payload = { user_ids: selectedUsers, action, plan: action === "upgrade" ? "pro" : null };
      const res = await credFetch(`${apiUrl}/api/admin/users/bulk-action`, { method: "POST", headers: getAuthHeaders(), credentials: "include", body: JSON.stringify(payload) });
      const data = await res.json();
      if (data.success) { toast.success(data.message); setSelectedUsers([]); loadUsers(); }
      else toast.error(data.detail || "Action failed");
    } catch (error) { toast.error("Bulk action failed"); }
  };

  const exportData = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/admin/export/users`, { headers: getAuthHeaders(), credentials: "include" });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "users_export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Users exported");
    } catch (error) { toast.error("Export failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportData}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
          <Input placeholder="Search users..." value={userSearch} onChange={(e) => setUserSearch(e.target.value)} className="w-64" />
          <Button onClick={loadUsers}><Search className="w-4 h-4" /></Button>
        </div>
      </div>

      {selectedUsers.length > 0 && (
        <Card className="p-4 bg-violet-50 border-violet-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-violet-700">{selectedUsers.length} users selected</span>
            <div className="flex gap-2">
              <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={() => handleBulkAction("upgrade")}>Upgrade to Pro</Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("downgrade")}>Downgrade to Free</Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("ban")}><Ban className="w-4 h-4 mr-1" /> Ban Selected</Button>
              <Button size="sm" variant="outline" onClick={() => handleBulkAction("unban")}><UserCheck className="w-4 h-4 mr-1" /> Unban Selected</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelectedUsers([])}>Clear Selection</Button>
            </div>
          </div>
        </Card>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox checked={users.length > 0 && selectedUsers.length === users.length} onCheckedChange={(checked) => { checked ? setSelectedUsers(users.map(u => u.id)) : setSelectedUsers([]); }} />
              </TableHead>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Checkbox checked={selectedUsers.includes(user.id)} onCheckedChange={(checked) => { checked ? setSelectedUsers([...selectedUsers, user.id]) : setSelectedUsers(selectedUsers.filter(id => id !== user.id)); }} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {user.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center"><Users className="w-4 h-4 text-violet-600" /></div>}
                    <span className="font-medium">{user.name || "User"}</span>
                  </div>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Select value={user.role || "user"} onValueChange={(value) => updateUserRole(user.id, value)}>
                    <SelectTrigger className="w-24 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select value={user.plan || "free"} onValueChange={(value) => updateUserPlan(user.id, value)}>
                    <SelectTrigger className={`w-28 h-8 ${user.plan === "pro" ? "bg-violet-100 text-violet-700 border-violet-300" : user.plan === "enterprise" ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-gray-100 text-gray-700"}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell><Badge className={user.status === "banned" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>{user.status || "Active"}</Badge></TableCell>
                <TableCell className="text-sm text-gray-500">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => getUserDetails(user.id)} title="View Details"><Eye className="w-4 h-4" /></Button>
                    {user.status === "banned" ? (
                      <Button size="sm" variant="ghost" className="text-green-600" onClick={() => unbanUser(user.id)} title="Unban"><UserCheck className="w-4 h-4" /></Button>
                    ) : (
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => banUser(user.id)} title="Ban"><Ban className="w-4 h-4" /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={showUserDetail} onOpenChange={setShowUserDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>User Details</DialogTitle></DialogHeader>
          {userDetail && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                {userDetail.user.picture ? <img src={userDetail.user.picture} alt="" className="w-16 h-16 rounded-full" /> : <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center"><Users className="w-8 h-8 text-violet-600" /></div>}
                <div>
                  <h3 className="font-semibold text-lg">{userDetail.user.name || "User"}</h3>
                  <p className="text-gray-500">{userDetail.user.email}</p>
                  <div className="flex gap-2 mt-1">
                    <Badge className={userDetail.user.plan === "pro" ? "bg-violet-100 text-violet-700" : "bg-gray-100 text-gray-700"}>{userDetail.user.plan || "Free"}</Badge>
                    <Badge className={userDetail.user.status === "banned" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>{userDetail.user.status || "Active"}</Badge>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Joined</p><p className="font-medium">{userDetail.user.created_at ? new Date(userDetail.user.created_at).toLocaleDateString() : "-"}</p></div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Last Login</p><p className="font-medium">{userDetail.user.last_login ? new Date(userDetail.user.last_login).toLocaleDateString() : "-"}</p></div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Role</p><p className="font-medium capitalize">{userDetail.user.role || "User"}</p></div>
                <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-500">Auth Type</p><p className="font-medium capitalize">{userDetail.user.auth_type || "Google"}</p></div>
              </div>
              {userDetail.user.login_history && userDetail.user.login_history.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Login History</h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {userDetail.user.login_history.slice().reverse().map((log, i) => (
                      <div key={i} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                        <span>{new Date(log.timestamp).toLocaleString()}</span>
                        <div className="flex gap-2">
                          <Badge className={log.action === "login" ? "bg-green-100 text-green-700" : log.action === "failed_login" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}>{log.action}</Badge>
                          <span className="text-gray-500">{log.method || log.device}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {userDetail.usage_history.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Recent Usage</h4>
                  <div className="space-y-1">
                    {userDetail.usage_history.slice(0, 5).map((u, i) => (
                      <div key={i} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                        <span>{u.date}</span>
                        <span>Searches: {u.keyword_searches} | AI: {u.ai_generations}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {userDetail.payment_history.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Payment History</h4>
                  <div className="space-y-1">
                    {userDetail.payment_history.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                        <span>{p.plan} - {"\u20B9"}{p.amount}</span>
                        <Badge className={p.status === "success" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}>{p.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
